import React, { useEffect, useState } from 'react';
import { X, ArrowClockwise, Sparkle } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useAppUpdate } from '../hooks/useAppUpdate';

// Point 3 — dismissible "Update available" popup for admin/handler sessions.
// Customers are excluded. If dismissed, it re-appears after the same poll
// interval (~18 min) while the newer version stays un-installed.
export const UpdateBanner = () => {
  const { user } = useAuth();
  const enabled = !!user && user.role !== 'customer';
  const { updateAvailable, applyUpdate, POLL_MS } = useAppUpdate(enabled);
  const [dismissed, setDismissed] = useState(false);
  const [applying, setApplying] = useState(false);

  // Re-show after the poll interval when the user dismisses without updating.
  useEffect(() => {
    if (!dismissed) return undefined;
    const id = setTimeout(() => setDismissed(false), POLL_MS);
    return () => clearTimeout(id);
  }, [dismissed, POLL_MS]);

  if (!enabled || !updateAvailable || dismissed) return null;

  const onUpdate = async () => {
    setApplying(true);
    await applyUpdate();
  };

  return (
    <div
      className="fixed z-[200] left-1/2 -translate-x-1/2 top-3 w-[94%] max-w-md"
      data-testid="update-banner"
    >
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl border border-white/10 px-4 py-3 flex items-center gap-3 animate-in slide-in-from-top-2">
        <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <Sparkle size={18} weight="fill" className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm leading-tight">New update available</div>
          <div className="text-[11px] text-slate-300 leading-tight">Refresh to get the latest version — you stay logged in.</div>
        </div>
        <button
          onClick={onUpdate}
          disabled={applying}
          data-testid="update-banner-update-btn"
          className="shrink-0 flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-2 rounded-xl disabled:opacity-60"
        >
          <ArrowClockwise size={15} weight="bold" className={applying ? 'animate-spin' : ''} />
          {applying ? 'Updating…' : 'Update'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          disabled={applying}
          data-testid="update-banner-close-btn"
          className="shrink-0 text-slate-400 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
};

export default UpdateBanner;
